import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Validate a rebus photo file before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateRebusPhoto(file) {
  if (!file) {
    return { valid: false, error: 'Ingen fil valt' };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: 'Ugyldig filtype. Bruk JPG, PNG, GIF eller WebP.' };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'Fila er for stor. Maks 10MB.' };
  }

  return { valid: true };
}

/**
 * Upload a photo for a rebus extra task
 * @param {File} file - The image file to upload
 * @param {string} sessionId - The rebus session ID
 * @param {string} teamId - The team ID
 * @param {string} taskId - The extra task ID
 * @returns {Promise<string>} The download URL
 */
export async function uploadRebusPhoto(file, sessionId, teamId, taskId) {
  const validation = validateRebusPhoto(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Get file extension
  const extension = file.name.split('.').pop().toLowerCase();
  const timestamp = Date.now();

  // Create storage reference
  const imagePath = `rebus-photos/${sessionId}/${teamId}/${taskId}-${timestamp}.${extension}`;
  const imageRef = ref(storage, imagePath);

  // Upload file
  await uploadBytes(imageRef, file);

  // Get and return download URL
  const downloadURL = await getDownloadURL(imageRef);
  return downloadURL;
}

/**
 * Delete a rebus photo from storage
 * @param {string} photoUrl - The full download URL of the photo
 * @returns {Promise<void>}
 */
export async function deleteRebusPhoto(photoUrl) {
  if (!photoUrl) return;

  try {
    // Extract the path from the URL
    const url = new URL(photoUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
    if (pathMatch) {
      const imagePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    }
  } catch (error) {
    console.error('Error deleting rebus photo:', error);
    // Don't throw - deletion failure shouldn't break the flow
  }
}
